import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Star, X, Quote } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const PROOF_QUOTES = [
  { name: "Priya & Karthik M.", text: "Conceived naturally within 4 months of following Fitly's fertility nutrition program." },
  { name: "Sneha R.", text: "Cycles became regular in 2 months. Lost 8 kgs and hormones finally balanced." },
  { name: "Anjali & Rahul S.", text: "Told IVF was our only option — now expecting twins naturally." },
  { name: "Roshni G.", text: "Fasting insulin dropped from 28 to 9 in 3 months. Truly life-changing." },
  { name: "Kavitha P.", text: "Stage 3 endometriosis — pain reduced dramatically. Now 14 weeks pregnant." },
  { name: "Deepika V.", text: "Failed 2 IVF cycles before Fitly. Third cycle was successful after their protocol." },
  { name: "Meenakshi K.", text: "Lost 12 kgs, thyroid normalized, conceived naturally after 2 years of struggling." },
  { name: "Fathima & Irfan Z.", text: "Joined from Dubai after 4 years of infertility. Conceived in month 5." },
  { name: "Swathi & Arun D.", text: "Trimester-wise nutrition plans gave us confidence. Our little one is thriving." },
  { name: "Lakshmi N.", text: "After 2 miscarriages, carried my baby to full term with Fitly's support." },
];

const PROGRAM_PAGES = ["/programs/elite", "/programs/premium-plus"];
const ROTATE_INTERVAL = 5000;

const Stars = () => (
  <div className="flex gap-px flex-shrink-0">
    {[...Array(5)].map((_, i) => (
      <Star key={`star-${i}`} size={10} className="fill-[#FBBC04] text-[#FBBC04]" />
    ))}
  </div>
);

export default function SmartTestimonialStrip() {
  const location = useLocation();
  const [index, setIndex] = useState(0);
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const isProgram = PROGRAM_PAGES.includes(location.pathname);

  // Reset on route change
  useEffect(() => {
    setDismissed(false);
    setShow(false);
    setIndex(Math.floor(Math.random() * PROOF_QUOTES.length));
  }, [location.pathname]);

  // Show after scroll
  useEffect(() => {
    if (!isProgram || dismissed) return;
    const check = () => setShow(window.scrollY > 400);
    window.addEventListener("scroll", check, { passive: true });
    check();
    return () => window.removeEventListener("scroll", check);
  }, [isProgram, dismissed]);

  // Auto-rotate
  const advance = useCallback(() => {
    setIndex((prev) => (prev + 1) % PROOF_QUOTES.length);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- PROOF_QUOTES is a module constant

  useEffect(() => {
    if (!show || dismissed) return;
    const timer = setInterval(advance, ROTATE_INTERVAL);
    return () => clearInterval(timer);
  }, [show, dismissed, advance]);

  if (!isProgram || dismissed || !show) return null;

  const quote = PROOF_QUOTES[index];

  return (
    <div className="fixed bottom-[52px] left-0 right-0 z-[39] pointer-events-none" data-testid="testimonial-strip">
      <div className="pointer-events-auto bg-[#132A20]/90 backdrop-blur-md border-t border-[#D4AF37]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-3">
          <Quote size={14} className="text-[#D4AF37]/60 flex-shrink-0 hidden sm:block" />
          <div className="flex-1 min-w-0 overflow-hidden relative h-[20px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.4 }}
                className="absolute inset-0 flex items-center gap-3"
              >
                <Stars />
                <p className="text-[#F7F5F0]/70 text-xs sm:text-sm truncate">
                  <span className="italic">&ldquo;{quote.text}&rdquo;</span>
                  <span className="text-[#D4AF37]/80 ml-2 font-medium not-italic hidden sm:inline">— {quote.name}</span>
                </p>
              </motion.div>
            </AnimatePresence>
          </div>
          <span className="text-[#D4AF37]/80 text-xs font-medium flex-shrink-0 sm:hidden">{quote.name}</span>
          <button
            onClick={() => setDismissed(true)}
            className="text-[#F7F5F0]/20 hover:text-[#F7F5F0]/60 transition-colors p-1 flex-shrink-0"
            aria-label="Dismiss testimonials"
            data-testid="testimonial-strip-dismiss"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
